'use client';

/**
 * A section's passage through the viewport, as CSS variables on the
 * section itself — the one input every scroll-linked effect on the homepage
 * reads, so the page moves as one piece rather than as a dozen listeners.
 *
 *   --enter  0 while the section's top is below the viewport; 1 once the top
 *            has travelled 90% of the way up.
 *   --exit   0 while the section's bottom is below the viewport; 1 once the
 *            bottom has travelled 90% of the way up.
 *   --scrolled  the page's own scroll, in viewport heights (0 at the top,
 *            capped at 1.5) — for a scene at the top of the page, whose
 *            depth should move from the very first pixel of scroll.
 *
 * Effects are plain CSS over those (`calc(var(--exit) * -80px)`), so they are
 * written with the rest of the styling and cost no React render per frame:
 * this writes three style properties inside one animation frame per scroll
 * event, and only while the section is on screen.
 *
 * Reduced motion never attaches a listener; the variables keep their resting
 * values from `.rt-scene` in globals.css (fully entered, not exiting), so
 * every effect sits at its settled state.
 */

import { useEffect, type RefObject } from 'react';

const RUN = 0.9;

export function useScrollScene(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    const paint = () => {
      frame = 0;
      const vh = window.innerHeight;
      const r = el.getBoundingClientRect();
      const enter = Math.min(1, Math.max(0, (vh - r.top) / (vh * RUN)));
      const exit = Math.min(1, Math.max(0, (vh - r.bottom) / (vh * RUN)));
      el.style.setProperty('--enter', enter.toFixed(4));
      el.style.setProperty('--exit', exit.toFixed(4));
      el.style.setProperty('--scrolled', Math.min(1.5, window.scrollY / vh).toFixed(4));
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(paint); };

    paint();
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        paint();
      } else {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    });
    io.observe(el);
    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref]);
}
